import React, { useEffect } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { injectIntl, FormattedMessage } from '../../util/reactIntl';
import { Page, LayoutSingleColumn, Button, NamedLink } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import css from './CustomLandingPage.module.css';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import CategoriesCard from './CategoriesCard/CategoriesCard';
import classNames from 'classnames';

import heroSectionImg from './heroSectionImg.webp';

import { LANDING_CATEGORIES } from './categories';
import HowItWorksSection from './HowItWorksSection/HowItWorksSection';
import NewsLetterSubscription from '../../components/NewsLetterSubscription/NewsLetterSubscription';
import { YoutubeEmbed } from '../PageBuilder/Primitives/YoutubeEmbed';
import WhoIsCauseeFor from './WhoIsCauseeFor/WhoIsCauseeFor';
import WhyCausee from './WhyCausee/WhyCausee';
import MoreCauses from './MoreCauses/MoreCauses';
import FoundingPartner from './FoundingPartner/FoundingPartner';
import AlreadySelling from './AlreadySelling/AlreadySelling';
import SellBand from './SellBand/SellBand';
import RouteMoney from './RouteMoney/RouteMoney';
import ImpactSection from './ImpactSection/ImpactSection';
import ListYourItemsButton from './ListYourItemsButton/ListYourItemsButton';
import AppBadges from './AppBadges/AppBadges';
// Charity-partnership enquiries block — disabled for the seller-focused launch.
// To restore it, uncomment this import and the <CharityPartnershipSection /> usage below.
// NOTE: leave this commented (with the usage) — an active import with no usage fails CI lint.
// import CharityPartnershipSection from './CharityPartnershipSection/CharityPartnershipSection';

function ManageFavouritesPageComponent(props) {
  const { intl, scrollingDisabled } = props;

  const title = intl.formatMessage({ id: 'CustomLandingPage.schemaTitle' });

  // When we arrive with a #more-causes hash (e.g. from the topbar "Causes" link
  // on another page), scroll to that section once it has rendered.
  useEffect(() => {
    if (typeof window === 'undefined' || window.location.hash !== '#more-causes') {
      return;
    }
    const timer = setTimeout(() => {
      const section = document.getElementById('more-causes');
      if (section) {
        section.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  // Smooth-scroll from the hero bullet to the "Why Causee?" section below.
  const scrollToWhyCausee = e => {
    e.preventDefault();
    const section = document.getElementById('why-causee');
    if (section) {
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <Page title={title} scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn
        topbar={
          <>
            <TopbarContainer />
          </>
        }
        footer={<FooterContainer hasNewsletter={false} />}
        topBarBackgroundColor="transparent"
      >
        <div className={css.root}>
          {/* SECTION 1 — Hero */}
          <div className={css.heroSection}>
            <div className={css.twoPanelView}>
              <div className={css.heroContent}>
                <h1>
                  <FormattedMessage id="CustomLandingPage.hero.title" />
                </h1>
                <h2 className={css.heroSubHeading}>
                  <FormattedMessage id="CustomLandingPage.hero.subheading" />
                </h2>
                <div className={css.heroDescriptionWrapper}>
                  <p>
                    <FormattedMessage id="CustomLandingPage.hero.description" />
                  </p>
                </div>
                <ul className={css.heroBullets}>
                  <li>
                    <FormattedMessage id="CustomLandingPage.hero.bullet1" />
                  </li>
                  <li>
                    <FormattedMessage id="CustomLandingPage.hero.bullet2" />
                  </li>
                  <li>
                    <a
                      href="#why-causee"
                      className={css.heroBulletLink}
                      onClick={scrollToWhyCausee}
                    >
                      <FormattedMessage id="CustomLandingPage.hero.bulletLink" />
                    </a>
                  </li>
                  <li>
                    <FormattedMessage
                      id="CustomLandingPage.hero.bullet4"
                      values={{
                        highlight: (
                          <strong>
                            <FormattedMessage id="CustomLandingPage.hero.bullet4.highlight" />
                          </strong>
                        ),
                      }}
                    />
                  </li>
                  <li>
                    <FormattedMessage
                      id="CustomLandingPage.hero.bullet5"
                      values={{
                        highlight: (
                          <strong>
                            <FormattedMessage id="CustomLandingPage.hero.bullet5.highlight" />
                          </strong>
                        ),
                      }}
                    />
                  </li>
                </ul>
                <ListYourItemsButton buttonClassName={css.heroActionButton}>
                  <FormattedMessage id="CustomLandingPage.hero.cta" />
                </ListYourItemsButton>

                {/* iOS / Android apps are not published yet, so these badges are
                    non-clickable placeholders under the hero CTA. */}
                <AppBadges />
              </div>

              <div className={css.heroImagePanel}>
                {/* The tag is a sibling of the photo card (not a child) so it can
                    sit just outside the card's curved top-left corner, as in the
                    design, without being clipped by the card's overflow. */}
                <div className={css.heroArt}>
                  <div className={css.heroImageWrapper}>
                    <img
                      src={heroSectionImg}
                      alt={intl.formatMessage({ id: 'CustomLandingPage.hero.imageAlt' })}
                      className={css.heroImage}
                    />
                  </div>
                  <span className={css.heroImageTag}>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.29 1.51 4.04 3 5.5l7 7Z" />
                    </svg>
                    {/* Two claims, one per line: what the seller keeps, and what
                        Causee gives away. */}
                    <span className={css.heroImageTagLines}>
                      <span>
                        <FormattedMessage id="CustomLandingPage.hero.imageTag" />
                      </span>
                      <span>
                        <FormattedMessage id="CustomLandingPage.hero.imageTagLine2" />
                      </span>
                    </span>
                  </span>
                </div>

                <p className={css.heroImageNote}>
                  <FormattedMessage id="CustomLandingPage.hero.imageNote" />
                </p>
              </div>
            </div>
          </div>

          {/* SECTION 2 — How a Causee sale becomes a donation (buyer/seller how-it-works) */}
          <div
            className={css.section}
            style={{ backgroundColor: 'var(--lb-cream-soft)', color: 'var(--lb-ink)' }}
          >
            <div className={css.contentWrapper}>
              <h2>
                <FormattedMessage id="CustomLandingPage.howItWorks.heading" />
              </h2>
              <h3 className={css.sectionSubHeading}>
                <FormattedMessage id="CustomLandingPage.howItWorks.subheading" />
              </h3>
              <HowItWorksSection />
            </div>
          </div>

          {/* SECTION 3 — The impact you create / Join us for our launch */}
          <ImpactSection />

          {/* SECTION 4 — Already selling on Vinted, eBay or Etsy? */}
          <AlreadySelling />

          {/* SECTION 5 — Become a Causee Founding Partner (pre-launch incentive) */}
          <FoundingPartner />

          {/* SECTION 6 — Why Causee? (anchor target for the hero bullet link) */}
          <div
            id="why-causee"
            className={classNames(css.section, css.whyCauseeAnchor)}
            style={{ backgroundColor: 'var(--lb-cream-soft)', color: 'var(--lb-ink)' }}
          >
            <div className={css.contentWrapper}>
              <h3 className={css.sectionSubHeading}>
                <FormattedMessage id="CustomLandingPage.whyCausee.eyebrow" />
              </h3>
              <h2>
                <FormattedMessage id="CustomLandingPage.whyCausee.heading" />
              </h2>
              <WhyCausee />
            </div>
          </div>

          {/* SECTION 7 — Who is Causee for? */}
          <div
            className={css.section}
            style={{ backgroundColor: 'var(--lb-green-mist)', color: 'var(--lb-ink)' }}
          >
            <div className={css.contentWrapper}>
              <h2>
                <FormattedMessage id="CustomLandingPage.whoIsFor.heading" />
              </h2>
              <h3 className={css.sectionSubHeading}>
                <FormattedMessage id="CustomLandingPage.whoIsFor.subheading" />
              </h3>
              <p>
                <FormattedMessage id="CustomLandingPage.whoIsFor.intro" />
              </p>
              <WhoIsCauseeFor />
            </div>
          </div>

          {/* SECTION 8 — Our ethical marketplace sells everything */}
          <div
            className={css.section}
            style={{ backgroundColor: 'var(--lb-cream)', color: 'var(--lb-ink)' }}
          >
            <div className={css.contentWrapper}>
              <h2>
                <FormattedMessage id="CustomLandingPage.categories.heading" />
              </h2>
              <h3 className={css.sectionSubHeading}>
                <FormattedMessage id="CustomLandingPage.categories.subheading" />
              </h3>
              <div className={css.categoryGrid}>
                {LANDING_CATEGORIES.map(cat => (
                  <CategoriesCard
                    key={cat.labelId}
                    icon={cat.icon}
                    labelId={cat.labelId}
                    category={cat.category}
                  />
                ))}
              </div>
              <NamedLink className={css.browseCta} name="SearchPage">
                <FormattedMessage id="CustomLandingPage.categories.cta" />
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M5 12h14" />
                  <path d="m13 6 6 6-6 6" />
                </svg>
              </NamedLink>
              <p className={css.launchDisclaimer}>
                <FormattedMessage
                  id="CustomLandingPage.launchDisclaimer"
                  values={{
                    terms: (
                      <NamedLink className={css.launchDisclaimerLink} name="TermsOfServicePage">
                        <FormattedMessage id="CustomLandingPage.launchDisclaimer.termsLink" />
                      </NamedLink>
                    ),
                  }}
                />
              </p>
            </div>
          </div>

          {/* SECTION 9 — For sellers: list it for free (design "sell band") */}
          <SellBand />

          {/* SECTION 10 — More Causes, Coming Soon (charity partners) */}
          <div id="more-causes">
            <MoreCauses />
          </div>

          {/* SECTION 11 — Partner with Causee (charity partnerships) */}
          <RouteMoney />

          {/* SECTION 12 — Together we can be A Force For Good (video) */}
          <div
            className={classNames(css.section, css.finalSection)}
            style={{ color: 'var(--lb-ink)' }}
          >
            <div className={css.contentWrapper}>
              <h2>
                <FormattedMessage id="CustomLandingPage.forceForGood.heading" />
              </h2>
              <h3 className={css.sectionSubHeading}>
                <FormattedMessage id="CustomLandingPage.forceForGood.subheading" />
              </h3>
              <p>
                <FormattedMessage id="CustomLandingPage.forceForGood.body" />
              </p>
              <div className={css.videoBackground}>
                <YoutubeEmbed youtubeVideoId="Q9k1snv-fZM" />
              </div>
              <NamedLink style={{ textDecoration: 'none' }} name="SignupPage">
                <Button className={css.sectionCta}>
                  <FormattedMessage id="CustomLandingPage.forceForGood.cta" />
                </Button>
              </NamedLink>
            </div>
          </div>

          {/* Charity-partnership enquiries — intentionally hidden for the seller-focused
              launch. Uncomment to bring it back. */}
          {/* <CharityPartnershipSection /> */}

          {/* Newsletter — wrapped in a mint section (like staging) so the floating
              card sits on the same light background with dark-blue text. */}
          <div
            className={classNames(css.section, css.newsletterBand)}
            style={{ backgroundColor: 'var(--lb-cream)', color: 'var(--lb-ink)' }}
          >
            <p className={css.newsletterLead}>
              <FormattedMessage id="CustomLandingPage.newsletter.lead" />
            </p>
            <div className={css.newsLetterSection}>
              <NewsLetterSubscription />
            </div>
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
}

const mapStateToProps = state => {
  return {
    scrollingDisabled: isScrollingDisabled(state),
  };
};

const mapDispatchToProps = dispatch => ({});

const CustomLandingPage = compose(
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl
)(ManageFavouritesPageComponent);

export default CustomLandingPage;
